import { sentenceCase } from "change-case";
import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Button,
  Card,
  Checkbox,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Menu,
  MenuItem,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
} from "@material-ui/core";
import { MoreVert, MailOutline } from "@material-ui/icons";
import { LoadingButton } from "@material-ui/lab";
import { createTheme, ThemeProvider } from "@material-ui/core/styles";
import { Icon } from "@iconify/react";
import plusFill from "@iconify/icons-eva/plus-fill";
import Scrollbar from "../components/Scrollbar";
import SearchNotFound from "../components/SearchNotFound";
import { TablaHead } from "../components/_dashboard/tablas";
import * as locales from "@material-ui/core/locale";
import Page from "../components/Page";
import Label from "../components/Label";
import AgregarCliente from "src/components/gestionCliente/AgregarCliente";
import { Titulo } from "../components/Titulo";
import AsociarEmpresas from "src/components/AsociarEmpresas";
import { useSelector } from "react-redux";
import { clientesApi } from "src/services/clientesApi";
import { empleadosApi } from "src/services/empleadosApi";
import { facturasApi } from "src/services/facturasApi";
import handleError from "src/utils/manejo-errores";
import {
  activarCalculoAutomatico,
  aplicarBordeUltimaFila,
  guardarArchivo,
} from "src/utils/excel-plantillas";
import ExcelJS from "exceljs";
import { RutFormat, formatRut } from "@fdograph/rut-utilities";
import { useSnackbar } from "notistack";
import StatusBadge from "src/components/cliente/badges/StatusBadge";
import useClientesFilters from "src/components/cliente/hooks/useClientesFilters";
import ClientesFilterPanel from "src/components/cliente/filters/ClientesFilterPanel";

const TABLE_HEAD = [
  { id: "rut", label: "Rut Cliente", alignRight: false },
  { id: "grupo", label: "Grupo", alignRight: false },
  { id: "nombre", label: "Nombre Cliente", alignRight: false },
  { id: "comuna", label: "Comuna", alignRight: false },
  { id: "telefono", label: "Teléfono", alignRight: false },
  { id: "correo", label: "Correo", alignRight: false },
  { id: "estado", label: "Estado", alignRight: false },
  { id: "portal", label: "Portal", alignRight: false, disableSorting: true },
  { id: "acciones", label: "", alignRight: false, disableSorting: true },
];

function isRoleVisible(role, visibleRoles) {
  return visibleRoles.includes(role);
}

function getFilename(role, user) {
  return role === "EMPLEADO"
    ? `Clientes ${user.nombre} ${user.apellidos}`
    : "Clientes";
}

function MenuAcciones({
  clientes,
  rolObtenido,
  user,
  manejarExportacionExcel,
  cargandoExportacionPromedioPago,
}) {
  const [anchorEl, setAnchorEl] = useState(null);
  const [exportandoClientes, setExportandoClientes] = useState(false);
  const open = Boolean(anchorEl);

  const handleExportarClientes = async () => {
    setAnchorEl(null);
    setExportandoClientes(true);
    try {
      const XLSX = await import("xlsx");
      const { formatRut, RutFormat } = await import("@fdograph/rut-utilities");

      const clientesParaExcel = clientes.map((cliente) => [
        formatRut(cliente.rut, RutFormat.DOTS_DASH),
        cliente.nombre,
        cliente.direccion || "Sin dirección",
        cliente.comuna || "Sin comuna",
        cliente.telefono || "Sin teléfono",
        cliente.correo || "Sin correo personal",
        cliente.correoFacturacion || "Sin correo de facturación",
        cliente.estado,
        cliente.grupo || "Sin grupo",
        cliente.tipoCliente || "Sin tipo cliente",
      ]);

      const worksheet = XLSX.utils.aoa_to_sheet([
        [
          "RUT",
          "Nombre",
          "Dirección",
          "Comuna",
          "Teléfono",
          "Correo Personal",
          "Correo Facturación",
          "Estado",
          "Grupo",
          "Tipo Cliente",
        ],
        ...clientesParaExcel,
      ]);

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Clientes");
      XLSX.writeFile(workbook, `${getFilename(rolObtenido, user)}.xlsx`);
    } catch {
      // silencioso — ExportToExcelPass ya muestra su propio error vía snackbar
    } finally {
      setExportandoClientes(false);
    }
  };

  const handleExportarPromedio = () => {
    setAnchorEl(null);
    manejarExportacionExcel();
  };

  return (
    <>
      <Button
        variant="outlined"
        size="small"
        endIcon={<MoreVert />}
        onClick={(e) => setAnchorEl(e.currentTarget)}
        style={{ marginRight: 8 }}
      >
        Acciones
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MenuItem
          onClick={handleExportarClientes}
          disabled={exportandoClientes || clientes.length === 0}
        >
          {exportandoClientes ? "Exportando..." : "Exportar clientes a Excel"}
        </MenuItem>
        {rolObtenido !== "EMPLEADO" && (
          <MenuItem
            onClick={handleExportarPromedio}
            disabled={cargandoExportacionPromedioPago}
          >
            {cargandoExportacionPromedioPago
              ? "Exportando..."
              : "Exportar promedio de pago"}
          </MenuItem>
        )}
      </Menu>
    </>
  );
}

export default function Clientes() {
  const {
    empleado: user,
    sucursal: sucursalObtenida,
    rol: rolObtenido,
  } = useSelector((state) => state.auth.user);
  const navigate = useNavigate();
  const [cargandoExportacionPromedioPago, setCargandoExportacionPromedioPago] =
    useState(false);

  // Portal — provisión masiva
  const [enviandoInvitaciones, setEnviandoInvitaciones] = useState(false);
  const [resultadoLote, setResultadoLote] = useState(null);
  const [openResultadoDialog, setOpenResultadoDialog] = useState(false);

  const [open, setOpen] = useState(false);
  const [openAsociarEmpresa, setOpenAsociarEmpresa] = useState(false);
  const [page, setPage] = useState(0);
  const [order, setOrder] = useState("asc");
  const [selected, setSelected] = useState([]);
  const [orderBy, setOrderBy] = useState("nombre");
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [clientes, setClientes] = useState([]);

  const {
    filtros,
    filtrosActivos,
    hayFiltrosActivos,
    updateFiltro,
    limpiarFiltros,
    removerFiltro,
  } = useClientesFilters();

  const [locale] = useState("esES");
  const [loadingTable, setLoadingTable] = useState(true);

  const queryParams = React.useMemo(
    () => ({
      page: page + 1,
      limit: rowsPerPage,
      sortBy: orderBy,
      sortOrder: order,
      ...(filtros.busqueda && { busqueda: filtros.busqueda }),
      ...(filtros.estadoCliente && { estado: filtros.estadoCliente }),
      ...(filtros.grupo && { grupo: filtros.grupo }),
      ...(filtros.estadoPortal !== "" && {
        portalActivo: filtros.estadoPortal,
      }),
    }),
    [page, rowsPerPage, orderBy, order, filtros],
  );

  const {
    data: searchDataAdmin,
    isFetching: isFetchingSearchAdmin,
    isSuccess: isSuccessSearchAdmin,
  } = clientesApi.useBuscarClientesQuery(queryParams, {
    skip: rolObtenido === "EMPLEADO",
  });

  const {
    data: searchDataEmpleado,
    isFetching: isFetchingSearchEmpleado,
    isSuccess: isSuccessSearchEmpleado,
  } = clientesApi.useBuscarClientesEmpleadoQuery(
    {
      idEmpleado: user.rut,
      sucursal: sucursalObtenida.refSucursal,
      ...queryParams,
    },
    {
      skip: rolObtenido !== "EMPLEADO",
    },
  );

  const searchData =
    rolObtenido === "EMPLEADO" ? searchDataEmpleado : searchDataAdmin;
  const isFetchingSearch =
    rolObtenido === "EMPLEADO"
      ? isFetchingSearchEmpleado
      : isFetchingSearchAdmin;
  const isSuccessSearch =
    rolObtenido === "EMPLEADO" ? isSuccessSearchEmpleado : isSuccessSearchAdmin;

  const [obtenerPromedioPagoClientes] =
    facturasApi.useLazyGetFacturasClientesQuery();

  const [addHistorialAcciones] = empleadosApi.useAddHistorialAccionesMutation();

  const { enqueueSnackbar } = useSnackbar();

  const [invitarPortalClienteLote] =
    clientesApi.useInvitarPortalClienteLoteMutation();

  const [invitarPortalCliente] = clientesApi.useInvitarPortalClienteMutation();

  const [invitandoRut, setInvitandoRut] = useState(null);

  const handleInvitarIndividual = useCallback(
    async (rut) => {
      setInvitandoRut(rut);
      try {
        await invitarPortalCliente({
          rut,
          refSucursal: sucursalObtenida.refSucursal,
        }).unwrap();
        enqueueSnackbar("Invitación enviada correctamente.", {
          variant: "success",
        });
      } catch (error) {
        enqueueSnackbar(
          error?.data?.message || "Error al enviar la invitación.",
          { variant: "error" },
        );
        handleError("handleInvitarIndividual", error);
      } finally {
        setInvitandoRut(null);
      }
    },
    [invitarPortalCliente, sucursalObtenida.refSucursal, enqueueSnackbar],
  );

  useEffect(() => {
    let urlActual = window.location.href;
    if (urlActual.includes("http://localhost:3000")) {
    } else {
      addHistorialAcciones({
        user: user.rut,
        body: {
          urlAccion: urlActual,
        },
      })
        .unwrap()
        .then(() => {})
        .catch(() => {});
    }
  }, [addHistorialAcciones, user.rut]);

  const [gruposList, setGruposList] = useState([]);
  const [totalClientesCount, setTotalClientesCount] = useState(0);

  useEffect(() => {
    setLoadingTable(true);
    if (isSuccessSearch && searchData) {
      setClientes(searchData.clientes || []);
      setGruposList(searchData.grupos || []);
      setTotalClientesCount(searchData.pagination?.total || 0);
      setLoadingTable(false);
    } else if (!isFetchingSearch) {
      setLoadingTable(false);
    }
  }, [searchData, isFetchingSearch, isSuccessSearch]);

  // Resetear a la primera página al cambiar cualquier filtro
  useEffect(() => {
    setPage(0);
  }, [filtros]);

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelecteds = clientes.map((n) => n.rut);
      setSelected(newSelecteds);
      return;
    }
    setSelected([]);
  };

  const handleSelectClick = (rut) => {
    const selectedIndex = selected.indexOf(rut);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = [...selected, rut];
    } else {
      newSelected = selected.filter((r) => r !== rut);
    }

    setSelected(newSelected);
  };

  const handleInvitarLote = async () => {
    if (selected.length === 0) return;

    const clientesInactivos = selected.filter((rut) => {
      const cliente = clientes.find((c) => c.rut === rut);
      return cliente?.estado === "INACTIVO";
    });

    if (clientesInactivos.length > 0) {
      enqueueSnackbar(
        `${clientesInactivos.length} cliente${clientesInactivos.length > 1 ? "s" : ""} inactivo${clientesInactivos.length > 1 ? "s" : ""} en la selección serán rechazados automáticamente.`,
        { variant: "warning" },
      );
    }

    setEnviandoInvitaciones(true);
    try {
      const resultado = await invitarPortalClienteLote({
        ruts: selected,
        refSucursal: sucursalObtenida.refSucursal,
      }).unwrap();

      setSelected([]);

      if (resultado.fallidos === 0) {
        enqueueSnackbar(
          `${resultado.exitosos} invitación${resultado.exitosos !== 1 ? "es" : ""} enviada${resultado.exitosos !== 1 ? "s" : ""} correctamente.`,
          { variant: "success" },
        );
      } else {
        setResultadoLote(resultado);
        setOpenResultadoDialog(true);
        if (resultado.exitosos > 0) {
          enqueueSnackbar(
            `${resultado.exitosos} enviada${resultado.exitosos !== 1 ? "s" : ""}, ${resultado.fallidos} con error. Ver detalle.`,
            { variant: "warning" },
          );
        } else {
          enqueueSnackbar(
            `No se pudo enviar ninguna invitación. Ver detalle.`,
            { variant: "error" },
          );
        }
      }
    } catch (error) {
      enqueueSnackbar("Ocurrió un error al enviar las invitaciones.", {
        variant: "error",
      });
      handleError("handleInvitarLote", error);
    } finally {
      setEnviandoInvitaciones(false);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const emptyRows =
    page > 0 ? Math.max(0, (1 + page) * rowsPerPage - totalClientesCount) : 0;

  const isUserNotFound = clientes.length === 0;

  /**
   *
   * @param {*} rut rut obtenido desde la base de datos
   * @returns retorna el rut en el formato xxxxxxxx-y, ya que
   * el rut es guardado sin guión en la base de datos
   */
  const formatearRut = (rut) => {
    let verificador = rut.charAt(rut.length - 1);
    let digitos = rut.substr(0, rut.length - 1);
    return digitos + "-" + verificador;
  };

  const manejarExportacionExcel = async () => {
    setCargandoExportacionPromedioPago(true);

    try {
      const { data: datosClientes } = await obtenerPromedioPagoClientes();

      if (datosClientes.length > 0) {
        const templatePath =
          "/static/plantillas/PLANTILLA_PROMEDIO_PAGOS_CLIENTES.xlsx";

        const workbook = await rellenarPlantilla(templatePath, datosClientes);

        await guardarArchivo(workbook, `Promedio de pago clientes.xlsx`);
        setCargandoExportacionPromedioPago(false);
      } else {
        lanzarError("No existen datos para exportar", "warning");
      }
    } catch (error) {
      lanzarError("Ocurrió un error desconocido al exportar Excel", "error");
      handleError("manejarExportacionExcel", error);
    }
  };

  const lanzarError = useCallback(
    (mensaje, variant = "error") => {
      enqueueSnackbar(mensaje, { variant });
      setCargandoExportacionPromedioPago(false);
    },
    [enqueueSnackbar],
  );

  return (
    <Page title="TAXTIC">
      <Container maxWidth="xl">
        <Titulo>
          <Grid container>
            <Grid item xs={12} md></Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="h4" gutterBottom>
                Clientes
              </Typography>
            </Grid>
            <Grid item xs={12} md>
              <Stack direction="row" justifyContent="flex-end" spacing={1}>
                {isRoleVisible(rolObtenido, ["ADMIN", "SUPER_ADMIN"]) && (
                  <MenuAcciones
                    clientes={clientes}
                    rolObtenido={rolObtenido}
                    user={user}
                    manejarExportacionExcel={manejarExportacionExcel}
                    cargandoExportacionPromedioPago={
                      cargandoExportacionPromedioPago
                    }
                  />
                )}
                {isRoleVisible(rolObtenido, [
                  "ADMIN",
                  "SUPER_ADMIN",
                  "SUPERVISOR",
                  "EMPLEADO",
                ]) && (
                  <Button
                    onClick={() => setOpen(true)}
                    variant="contained"
                    startIcon={<Icon icon={plusFill} />}
                  >
                    Agregar Cliente
                  </Button>
                )}
              </Stack>
            </Grid>
          </Grid>
        </Titulo>
        <Card>
          <ClientesFilterPanel
            filtros={filtros}
            filtrosActivos={filtrosActivos}
            hayFiltrosActivos={hayFiltrosActivos}
            updateFiltro={updateFiltro}
            limpiarFiltros={limpiarFiltros}
            removerFiltro={removerFiltro}
            grupos={gruposList}
            totalClientes={totalClientesCount}
          />

          {isRoleVisible(rolObtenido, ["ADMIN", "SUPER_ADMIN"]) &&
            selected.length > 0 && (
              <Stack
                direction="row"
                alignItems="center"
                style={{ padding: "0 16px 12px 16px" }}
              >
                <Button
                  variant="contained"
                  color="primary"
                  disabled={enviandoInvitaciones}
                  onClick={handleInvitarLote}
                  startIcon={
                    enviandoInvitaciones ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : null
                  }
                >
                  {enviandoInvitaciones
                    ? "Enviando..."
                    : `Enviar invitaciones (${selected.length})`}
                </Button>
              </Stack>
            )}

          <Scrollbar>
            <TableContainer sx={{ minWidth: 800 }}>
              {!loadingTable ? (
                <Table>
                  <TablaHead
                    order={order}
                    orderBy={orderBy}
                    headLabel={TABLE_HEAD}
                    // La página actual, no el total paginado: seleccionar todo marca solo
                    // las filas visibles, y con el total la casilla nunca queda checked,
                    // así que el clic para desmarcar volvía a seleccionarlas.
                    rowCount={clientes.length}
                    select={true}
                    selectedRows={selected}
                    handleSelectAllClick={handleSelectAllClick}
                    onRequestSort={handleRequestSort}
                  />
                  <TableBody>
                    {clientes.map((row) => {
                      const {
                        rut,
                        grupo,
                        nombre,
                        comuna,
                        telefono,
                        correo,
                        estado,
                        portalActivo,
                      } = row;
                      const isItemSelected = selected.indexOf(rut) !== -1;

                      return (
                        <TableRow
                          hover
                          key={rut}
                          tabIndex={-1}
                          role="checkbox"
                          selected={isItemSelected}
                          aria-checked={isItemSelected}
                        >
                          <TableCell padding="checkbox">
                            <Checkbox
                              checked={isItemSelected}
                              onChange={() => handleSelectClick(rut)}
                            />
                          </TableCell>
                          <TableCell
                            align="center"
                            component="th"
                            scope="row"
                            onClick={() => {
                              navigate("/dashboard/clientes/" + row.rut);
                            }}
                            style={{ cursor: "pointer" }}
                          >
                            <Typography variant="subtitle2" noWrap>
                              {formatearRut(rut)}
                            </Typography>
                          </TableCell>
                          <TableCell
                            align="left"
                            onClick={() => {
                              navigate("/dashboard/clientes/" + row.rut);
                            }}
                            style={{ cursor: "pointer" }}
                          >
                            {grupo ? grupo : ""}
                          </TableCell>
                          <TableCell
                            align="left"
                            onClick={() => {
                              navigate("/dashboard/clientes/" + row.rut);
                            }}
                            style={{ cursor: "pointer" }}
                          >
                            {nombre}
                          </TableCell>
                          <TableCell
                            align="left"
                            onClick={() => {
                              navigate("/dashboard/clientes/" + row.rut);
                            }}
                            style={{ cursor: "pointer" }}
                          >
                            {comuna}
                          </TableCell>
                          <TableCell
                            align="left"
                            onClick={() => {
                              navigate("/dashboard/clientes/" + row.rut);
                            }}
                            style={{ cursor: "pointer" }}
                          >
                            {telefono}
                          </TableCell>
                          <TableCell
                            align="left"
                            onClick={() => {
                              navigate("/dashboard/clientes/" + row.rut);
                            }}
                            style={{ cursor: "pointer" }}
                          >
                            {correo}
                          </TableCell>
                          <TableCell align="left">
                            <Label
                              variant="ghost"
                              color={
                                (estado === "INACTIVO" && "error") || "success"
                              }
                            >
                              {sentenceCase(estado)}
                            </Label>
                          </TableCell>
                          <TableCell align="left">
                            <StatusBadge
                              estado={portalActivo}
                              tipo="portal"
                              showDot={false}
                            />
                          </TableCell>
                          <TableCell align="right" padding="none">
                            <Tooltip
                              title={
                                portalActivo
                                  ? "Reenviar invitación"
                                  : "Enviar invitación"
                              }
                            >
                              <span>
                                <LoadingButton
                                  size="small"
                                  loading={invitandoRut === rut}
                                  disabled={invitandoRut !== null}
                                  onClick={() => handleInvitarIndividual(rut)}
                                >
                                  <MailOutline fontSize="small" />
                                </LoadingButton>
                              </span>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                    {emptyRows > 0 && (
                      <TableRow style={{ height: 53 * emptyRows }}>
                        <TableCell colSpan={10} />
                      </TableRow>
                    )}
                  </TableBody>
                  {isUserNotFound && (
                    <TableBody>
                      <TableRow>
                        <TableCell align="center" colSpan={10} sx={{ py: 3 }}>
                          <SearchNotFound searchQuery={filtros.busqueda} />{" "}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  )}
                </Table>
              ) : (
                <Skeleton variant="rectangular" width="100%" height="500px" />
              )}
            </TableContainer>
          </Scrollbar>

          <ThemeProvider
            theme={(outerTheme) => createTheme(outerTheme, locales[locale])}
          >
            <TablePagination
              rowsPerPageOptions={[10, 25, 50]}
              component="div"
              count={totalClientesCount}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </ThemeProvider>
        </Card>
      </Container>

      {open && <AgregarCliente open={open} setOpen={setOpen} />}

      {openAsociarEmpresa && (
        <AsociarEmpresas
          open={openAsociarEmpresa}
          setOpen={setOpenAsociarEmpresa}
        />
      )}

      <Dialog
        open={openResultadoDialog}
        onClose={() => setOpenResultadoDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Resultado de invitaciones</DialogTitle>
        <DialogContent>
          {resultadoLote && (
            <>
              <Typography variant="body2" gutterBottom>
                <strong>Exitosas:</strong> {resultadoLote.exitosos}{" "}
                &nbsp;|&nbsp;
                <strong>Con error:</strong> {resultadoLote.fallidos}
              </Typography>
              {resultadoLote.detalle
                .filter((d) => !d.ok)
                .map((d) => (
                  <Typography
                    key={d.rut}
                    variant="body2"
                    style={{ marginTop: 8 }}
                  >
                    <strong>{formatearRut(d.rut)}</strong> - {d.mensaje}
                  </Typography>
                ))}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setOpenResultadoDialog(false)}
            color="primary"
            variant="contained"
          >
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>
    </Page>
  );
}

const rellenar = (
  worksheet,
  datosTabla,
  tituloTabla,
  filaInicial,
  columnas,
) => {
  try {
    const {
      HEADER_TITLE,
      TITLE_FONT_STYLE,
      HEADER_FILL,
      HEADER_FONT_STYLE,
      DATA_FONT_STYLE,
    } = CONSTANTES_EXCEL;

    const primeraColumna = columnas[0];
    const ultimaColumna = columnas[columnas.length - 1];

    worksheet.getCell(`${primeraColumna.id}${filaInicial - 1}`).value =
      tituloTabla;
    worksheet.mergeCells(
      `${primeraColumna.id}${filaInicial - 1}`,
      `${ultimaColumna.id}${filaInicial - 1}`,
    );
    worksheet.getCell(`${primeraColumna.id}${filaInicial - 1}`).alignment = {
      vertical: "middle",
      horizontal: "center",
    };
    worksheet.getCell(`${primeraColumna.id}${filaInicial - 1}`).fill =
      HEADER_TITLE;
    worksheet.getCell(`${primeraColumna.id}${filaInicial - 1}`).font =
      TITLE_FONT_STYLE;

    columnas.forEach((columna) => {
      worksheet.getCell(`${columna.id}${filaInicial}`).value = columna.titulo;
      worksheet.getCell(`${columna.id}${filaInicial}`).fill = HEADER_FILL;
      worksheet.getCell(`${columna.id}${filaInicial}`).font = HEADER_FONT_STYLE;
      worksheet.getCell(`${columna.id}${filaInicial}`).alignment = {
        vertical: "middle",
        horizontal: "center",
      };
    });

    datosTabla.forEach((dato, index) => {
      const rowIndex = filaInicial + 1 + index;
      columnas.forEach((columna) => {
        const valor = dato[columna.propiedad];
        const cell = worksheet.getCell(`${columna.id}${rowIndex}`);
        cell.font = DATA_FONT_STYLE;
        cell.value = valor;

        // Aplicar formato numérico si el valor es un número
        if (!isNaN(valor)) {
          cell.value = valor;
          cell.numFmt = "0";
        }
        if (columna.propiedad === "refCliente") {
          const rut = formatRut(valor, RutFormat.DOTS_DASH);
          cell.value = rut;
          cell.numFmt = "@"; // Se mantiene formato string en Excel
        }
        if (columna.propiedad === "nombreCliente") {
          cell.alignment = {
            vertical: "middle",
            horizontal: "left",
          };
        } else {
          cell.alignment = {
            vertical: "middle",
            horizontal: "center",
          };
        }
      });
    });

    worksheet.columns.forEach((column, columnIndex) => {
      let maxLength = 0;
      column.eachCell({ includeEmpty: true }, (cell) => {
        const text = cell.text || "";
        maxLength = Math.max(maxLength, text.length);
      });
      column.width = maxLength + 7;
    });
  } catch (error) {
    handleError("rellenar", error);
  }
};

const rellenarTabla = (worksheet, datos, tituloTabla) => {
  try {
    let columnas = [
      { id: "E", titulo: "RUT cliente", propiedad: "refCliente" },
      { id: "F", titulo: "Cliente", propiedad: "nombreCliente" },
      { id: "G", titulo: "Mediana días de pago", propiedad: "medianaDiasPago" },
    ];

    const filaInicialTabla = 8;

    rellenar(worksheet, datos, tituloTabla, filaInicialTabla, columnas);
  } catch (error) {
    handleError("rellenarTabla", error);
  }
};

const rellenarPlantilla = async (templatePath, datos) => {
  try {
    // Crear un nuevo objeto Workbook de ExcelJS
    const workbook = new ExcelJS.Workbook();

    // Cargar la plantilla Excel desde el archivo en la ruta especificada
    const templateBuffer = await fetch(templatePath).then((response) =>
      response.arrayBuffer(),
    );
    await workbook.xlsx.load(templateBuffer);

    // Habilitar el cálculo automático en la hoja
    activarCalculoAutomatico(workbook);

    const worksheetFacturaPlanes = workbook.worksheets[0]; // Hoja de Factura Planes

    const nombre = "Promedio de pagos";
    worksheetFacturaPlanes.name = nombre;
    rellenarTabla(worksheetFacturaPlanes, datos, nombre, "PLAN");
    aplicarBordeUltimaFila(worksheetFacturaPlanes, 0, 11);

    workbook.views = {
      activeTab: 0,
    };
    return workbook;
  } catch (error) {
    handleError("rellenarPlantilla", error);
  }
};

const CONSTANTES_EXCEL = {
  HEADER_FONT_STYLE: {
    name: "Arial Nova Cond",
    size: 12,
    bold: true,
    color: { argb: "44546A" },
  },
  TITLE_FONT_STYLE: {
    name: "Arial Nova Cond",
    size: 12,
    bold: true,
    color: { argb: "FFFFFF" },
  },
  DATA_FONT_STYLE: {
    name: "Arial Nova Cond",
    size: 12,
  },
  HEADER_FILL: {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: "ED7D31" },
  },
  HEADER_TITLE: {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: "D9D9D9" },
  },
};
